import type { EmailTemplate } from '@/mocks/superAdminEmailTemplates';

interface EmailTemplateCardProps {
  template: EmailTemplate;
  onClick: (t: EmailTemplate) => void;
  onDuplicate: (t: EmailTemplate) => void;
}

export default function EmailTemplateCard({ template, onClick, onDuplicate }: EmailTemplateCardProps) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden hover:border-foreground-300 transition-all duration-200 group">
      {/* Mini header bar */}
      <div className="bg-background-100 border-b border-background-200 px-4 py-2 flex items-center gap-2">
        <div className="w-4 h-4 rounded bg-foreground-950 flex items-center justify-center shrink-0">
          <i className="ri-radio-line text-white text-[7px]"></i>
        </div>
        <span className="text-foreground-600 text-[9px] font-semibold">RadioFrecuenciaWeb</span>
      </div>

      {/* Mini hero bar */}
      <div
        className="h-1.5 w-full"
        style={{ backgroundColor: template.heroBgColor }}
      ></div>

      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <button
            onClick={() => onClick(template)}
            className="flex items-center gap-2.5 min-w-0 text-left flex-1 cursor-pointer"
          >
            <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
              <i className={`${template.icon} text-foreground-500 text-sm`}></i>
            </div>
            <div className="min-w-0">
              <p className="text-foreground-800 text-xs font-semibold truncate">{template.label}</p>
              <p className="text-foreground-400 text-[10px] truncate">{template.subject}</p>
            </div>
          </button>
          <div className="flex items-center gap-1 shrink-0">
            <span className={`px-2 py-0.5 rounded-full border text-[9px] font-medium ${template.badgeColor}`}>
              {Object.keys(template.variables).length} vars
            </span>
          </div>
        </div>

        {/* Preview snippet */}
        <button
          onClick={() => onClick(template)}
          className="w-full text-left bg-background-100 rounded-lg overflow-hidden border border-background-200/70 cursor-pointer group/preview"
        >
          <div
            className="h-14 flex items-center justify-center text-[9px] text-white font-semibold tracking-wide relative overflow-hidden px-4"
            style={{ backgroundColor: template.heroBgColor }}
          >
            <div className="absolute inset-0" style={{ backgroundColor: template.heroOverlayColor }}></div>
            <div className="relative z-10 text-center space-y-0.5">
              <span className="block truncate max-w-[180px] text-[10px]">{template.title}</span>
              <span className="block truncate max-w-[200px] text-white/70 text-[8px] font-normal">{template.subtitle}</span>
            </div>
          </div>
          <div className="bg-white border-t border-background-200/70 p-2.5 space-y-1.5">
            <div className="w-3/4 h-1.5 bg-background-200 rounded"></div>
            <div className="w-full h-1.5 bg-background-200 rounded"></div>
            <div className="w-2/3 h-1.5 bg-background-200 rounded"></div>
            {template.showCta && (
              <div
                className="inline-block px-3 py-1 rounded-md mt-1 text-[8px] font-bold text-white transition-transform group-hover/preview:scale-105"
                style={{ backgroundColor: template.ctaColor }}
              >
                {template.ctaText}
              </div>
            )}
          </div>
          {/* Mini footer */}
          <div className="bg-background-50 border-t border-background-200/40 px-2.5 py-1.5 flex items-center justify-center gap-3 text-foreground-400">
            <i className="ri-global-line text-[7px]"></i>
            <i className="ri-customer-service-2-line text-[7px]"></i>
            <i className="ri-facebook-fill text-[7px]"></i>
            <i className="ri-instagram-fill text-[7px]"></i>
          </div>
        </button>

        {/* Footer info & actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-[10px] text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-time-line text-[9px]"></i>
              {template.lastEdited}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line text-[9px]"></i>
              {template.lastEditedBy}
            </span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate(template); }}
            className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors opacity-0 group-hover:opacity-100"
            title="Duplicar template"
          >
            <i className="ri-file-copy-line text-foreground-500 text-xs"></i>
          </button>
        </div>

        {/* Active version badge */}
        <div className="flex items-center gap-1.5 text-[9px] text-foreground-400">
          <i className="ri-git-branch-line text-[9px]"></i>
          <span>v{template.activeVersion} activa</span>
          <span className="text-foreground-300">·</span>
          <span>{template.versions.length} versión{template.versions.length !== 1 ? 'es' : ''}</span>
        </div>
      </div>
    </div>
  );
}